
import { useState, useEffect } from "react";

interface Message {
  emoji: string;
  text: string;
}

interface LoadingMessageProps {
  messages: Message[];
  overtimeMessages: Message[];
  isOvertime: boolean;
  messageChangeInterval: number;
  blinkDuration: number;
}

export function LoadingMessage({
  messages,
  overtimeMessages,
  isOvertime,
  messageChangeInterval,
  blinkDuration,
}: LoadingMessageProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);
  const [usedMessageIndexes, setUsedMessageIndexes] = useState<number[]>([]);
  const [usedOvertimeIndexes, setUsedOvertimeIndexes] = useState<number[]>([]);
  const [isGoingUp, setIsGoingUp] = useState(true);
  const [animationProgress, setAnimationProgress] = useState(0);

  const getNextMessageIndex = (currentMessages: Message[], usedIndexes: number[]) => {
    if (currentMessages.length === 0) return 0;
    
    const availableIndexes = Array.from(
      { length: currentMessages.length },
      (_, i) => i
    ).filter(i => !usedIndexes.includes(i));

    if (availableIndexes.length === 0) {
      return 0;
    }

    const randomIndex = Math.floor(Math.random() * availableIndexes.length);
    return availableIndexes[randomIndex];
  };

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setIsBlinking(true);
      
      setTimeout(() => {
        if (isOvertime && overtimeMessages.length > 0) {
          const nextIndex = getNextMessageIndex(overtimeMessages, usedOvertimeIndexes);
          setMessageIndex(nextIndex);
          setUsedOvertimeIndexes(prev => 
            prev.length >= overtimeMessages.length ? [nextIndex] : [...prev, nextIndex]
          );
        } else if (messages.length > 0) {
          const nextIndex = getNextMessageIndex(messages, usedMessageIndexes);
          setMessageIndex(nextIndex);
          setUsedMessageIndexes(prev => 
            prev.length >= messages.length ? [nextIndex] : [...prev, nextIndex]
          );
        }
        setIsBlinking(false);
      }, blinkDuration);
    }, messageChangeInterval);

    return () => clearInterval(messageInterval);
  }, [isOvertime, messages, overtimeMessages, messageChangeInterval, blinkDuration, usedMessageIndexes, usedOvertimeIndexes]);

  // Continuous floating animation
  useEffect(() => {
    const animationFrame = requestAnimationFrame(function animate() {
      setAnimationProgress(prev => {
        if (isGoingUp) {
          if (prev >= 1) {
            setIsGoingUp(false);
            return 0.99;
          }
          return prev + 0.02;
        } else {
          if (prev <= 0) {
            setIsGoingUp(true);
            return 0.01;
          }
          return prev - 0.02;
        }
      });
      requestAnimationFrame(animate);
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [isGoingUp]);

  // Verificação de segurança para evitar acesso a propriedades undefined
  const currentMessages = isOvertime ? overtimeMessages : messages;
  const currentMessage = currentMessages[messageIndex];
  
  // Se não há mensagem válida, retorna um fallback
  if (!currentMessage) {
    return (
      <div className="flex flex-col items-center gap-6">
        <span className="text-6xl">⏳</span>
        <p className="text-2xl font-medium text-center text-gray-800">
          Carregando...
        </p>
      </div>
    );
  }

  const translateY = -10 + (animationProgress * 20); // Range from -10px to 10px

  return (
    <div 
      className={`flex flex-col items-center gap-6 transition-opacity duration-300 ${
        isBlinking ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <span 
        className="text-6xl inline-block transform-gpu"
        style={{ 
          transform: `translateY(${translateY}px)`,
          transition: 'transform 0.1s linear'
        }}
      >
        {currentMessage.emoji}
      </span>
      <p className="text-2xl font-medium text-center text-gray-800">
        {currentMessage.text}
      </p>
    </div>
  );
}
